// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Shunt — the yard, drawn as DOM. A board game with letters on the pieces wants crisp text, real
// hit targets and a layout that reflows itself, so the roads are elements and the wagons are one
// absolutely-positioned layer on top of them, moved with `transform` so a rake slides rather than
// teleports. Nothing here knows the rules; it draws a Yard and reports taps.
//
// LAYOUT. Every road is left-aligned at a shared mouth, with a throat spine drawn down the left
// edge joining them — the true geometry (roads fanning out of a throat, the headshunt running the
// other way) costs twice the width on a phone, and the spine reads as a throat immediately. The
// kickback road is indented and joined to its PARENT road rather than to the spine, which is how
// you can see at a glance that you must go through Road A to reach it.
//
// SIZING. Slot width comes from the widest road the mode can present PLUS one for the loco, so
// the fullest possible yard still fits the viewport with no horizontal overflow — the failure
// that shipped a broken 9x9 board in a sibling game. Everything is measured from the host's real
// box, never assumed, and a zero-size measurement (a backgrounded tab reports clientWidth 0) is
// ignored and retried rather than turned into NaN geometry.

import { LIVERY, letterOf } from './palette';
import { childrenOf, ROOT, type Yard } from './rules';

export interface TapTarget {
  /** A whole road: tapping it propels the rake in (or backs the loco out onto its parent). */
  readonly kind: 'road' | 'car' | 'loco';
  readonly track: number;
  /** For 'car': the car's index within its road. */
  readonly index?: number;
}

export interface Geometry {
  slot: number;
  rowH: number;
  carH: number;
  carTop: number;
  gutter: number;
  indent: number;
  width: number;
  height: number;
}

const MIN_SLOT = 44; // the hit-target floor; the board scrolls its own container before going under this
const MAX_SLOT = 84;
const MAX_ROW = 104;

export class YardView {
  private readonly host: HTMLElement;
  private roadsLayer!: HTMLElement;
  private carsLayer!: HTMLElement;
  private readonly carEls = new Map<number, HTMLElement>();
  private locoEl!: HTMLElement;
  private roadEls: HTMLElement[] = [];
  private geom: Geometry = { slot: 56, rowH: 58, carH: 42, carTop: 6, gutter: 38, indent: 0, width: 0, height: 0 };
  private yard: Yard | null = null;
  private legalRoads = new Set<number>();
  private legalCars = new Set<string>();
  private locoTappable = false;

  constructor(host: HTMLElement) {
    this.host = host;
    this.host.classList.add('yard-host');
  }

  /** Build the static roads for a yard shape. Safe to call again for a fresh round. */
  mount(yard: Yard): void {
    this.yard = yard;
    this.host.innerHTML = `<div class="yard-roads"></div><div class="yard-cars"></div>`;
    this.roadsLayer = this.host.querySelector('.yard-roads')!;
    this.carsLayer = this.host.querySelector('.yard-cars')!;
    this.carEls.clear();
    this.roadEls = [];

    const spec = yard.spec;
    for (let t = 0; t < spec.tracks.length; t++) {
      const track = spec.tracks[t];
      const el = document.createElement('div');
      el.className = 'road';
      el.dataset.track = String(t);
      if (t === ROOT) el.classList.add('road-headshunt');
      if (t === spec.departure) el.classList.add('road-departure');
      if (track.parent !== null && track.parent !== ROOT) el.classList.add('road-kickback');
      el.innerHTML = `
        <span class="road-label">${track.label}</span>
        <span class="road-line"><span class="road-rail"></span><span class="road-stop"></span></span>
        <span class="road-glow" aria-hidden="true"></span>`;
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', track.name);
      this.roadsLayer.appendChild(el);
      this.roadEls.push(el);
    }

    const spine = document.createElement('div');
    spine.className = 'yard-spine';
    spine.setAttribute('aria-hidden', 'true');
    this.roadsLayer.appendChild(spine);

    for (const id of allCarIds(yard)) {
      const el = document.createElement('div');
      el.className = 'car';
      el.dataset.car = String(id);
      el.style.background = LIVERY[id % LIVERY.length];
      el.innerHTML = `<span class="car-letter">${letterOf(id)}</span><span class="car-wheels" aria-hidden="true"></span>`;
      this.carsLayer.appendChild(el);
      this.carEls.set(id, el);
    }

    this.locoEl = document.createElement('div');
    this.locoEl.className = 'loco';
    this.locoEl.innerHTML = `<span class="loco-cab" aria-hidden="true"></span><span class="loco-stack" aria-hidden="true"></span><span class="car-wheels" aria-hidden="true"></span>`;
    this.locoEl.setAttribute('aria-label', 'Locomotive');
    this.carsLayer.appendChild(this.locoEl);

    this.resize();
  }

  /**
   * Recompute geometry from the host's real box. A zero measurement means the tab is hidden or the
   * element is not laid out yet; ignoring it keeps NaN coordinates out of the transforms.
   */
  resize(): boolean {
    const yard = this.yard;
    if (!yard) return false;
    // Measure the PARENT, never the host itself: the host's width is set from this calculation, so
    // measuring it would feed its own output back in and shrink the board a little on every resize.
    const parent = this.host.parentElement;
    const w = parent ? parent.clientWidth || parent.getBoundingClientRect().width : this.host.clientWidth;
    if (!w || w < 2) return false;

    const spec = yard.spec;
    const gutter = w < 380 ? 34 : 40;
    const hasKick = spec.tracks.some((t) => t.parent !== null && t.parent !== ROOT);
    const indentSlots = hasKick ? 1.4 : 0;

    // The widest presentation any road can be asked to show: its capacity, plus the loco, plus the
    // kickback indent if it applies to that road.
    let widest = 1;
    for (let t = 0; t < spec.tracks.length; t++) {
      const isKick = spec.tracks[t].parent !== null && spec.tracks[t].parent !== ROOT;
      widest = Math.max(widest, spec.tracks[t].cap + 1 + (isKick ? indentSlots : 0));
    }

    const avail = Math.max(80, w - gutter - 12);
    const slot = Math.max(MIN_SLOT, Math.min(MAX_SLOT, Math.floor((avail / widest) * 10) / 10));

    // Rows grow to fill the height the screen actually offers, so the yard is not a small strip
    // marooned in the middle of a tall phone and every road stays a fat tap target. The WAGON
    // does not stretch with the row — it keeps a wagon's proportions and is centred in it.
    const parentH = parent?.clientHeight ?? 0;
    const roomPerRow = parentH > 40 ? Math.floor((parentH - 6) / spec.tracks.length) : 0;
    const rowH = Math.round(Math.max(54, Math.min(MAX_ROW, roomPerRow || slot * 1.06)));
    const carH = Math.round(Math.max(44, Math.min(rowH - 16, slot * 0.82)));
    const carTop = Math.round(Math.max(4, (rowH - carH - 8) / 2));

    // On a wide screen the slot hits its ceiling long before the width runs out, so the board is
    // sized to the yard it actually draws and centred — otherwise the roads huddle on the left of
    // a box half of which is empty.
    const boardW = Math.min(w, Math.round(gutter + widest * slot + 10));

    this.geom = {
      slot,
      rowH,
      carH,
      carTop,
      gutter,
      indent: indentSlots * slot,
      width: boardW,
      height: rowH * spec.tracks.length,
    };

    this.host.style.width = `${boardW}px`;

    this.host.style.setProperty('--slot', `${slot}px`);
    this.host.style.setProperty('--rowh', `${rowH}px`);
    this.host.style.setProperty('--carh', `${carH}px`);
    this.host.style.setProperty('--cartop', `${carTop}px`);
    this.host.style.setProperty('--gutter', `${gutter}px`);
    this.host.style.setProperty('--indent', `${this.geom.indent}px`);

    for (let t = 0; t < this.roadEls.length; t++) {
      const isKick = spec.tracks[t].parent !== null && spec.tracks[t].parent !== ROOT;
      const left = gutter + (isKick ? this.geom.indent : 0);
      // A road has to be drawn long enough to hold the loco AND its wagon capacity, because the
      // loco stands at the mouth while it works — draw it to `cap` alone and the outermost wagon
      // hangs off the end of its own road the moment the loco arrives.
      const width = (spec.tracks[t].cap + 1) * slot + 6;
      this.roadEls[t].style.left = `${left}px`;
      this.roadEls[t].style.top = `${t * rowH}px`;
      this.roadEls[t].style.width = `${Math.min(width, boardW - left - 4)}px`;
      this.roadEls[t].style.height = `${rowH}px`;
    }
    this.host.style.height = `${this.geom.height}px`;
    this.paint();
    return true;
  }

  /** Where a piece standing at `index` on `track` sits, in host-local pixels. */
  private slotXY(track: number, index: number): { x: number; y: number } {
    const spec = this.yard!.spec;
    const isKick = spec.tracks[track].parent !== null && spec.tracks[track].parent !== ROOT;
    const x = this.geom.gutter + (isKick ? this.geom.indent : 0) + index * this.geom.slot;
    const y = track * this.geom.rowH + this.geom.carTop;
    return { x, y };
  }

  /** Draw the current position. Cheap enough to call on every change. */
  paint(): void {
    const yard = this.yard;
    if (!yard || !this.geom.width) return;

    for (let t = 0; t < yard.cars.length; t++) {
      const onThisRoad = yard.locoAt === t;
      const offset = onThisRoad ? 1 : 0;
      const road = yard.cars[t];
      for (let i = 0; i < road.length; i++) {
        const el = this.carEls.get(road[i]);
        if (!el) continue;
        const { x, y } = this.slotXY(t, i + offset);
        el.style.transform = `translate(${x}px, ${y}px)`;
        el.dataset.track = String(t);
        el.dataset.index = String(i);
        const tappable = this.legalCars.has(`${t}:${i}`);
        el.classList.toggle('tappable', tappable);
        el.setAttribute('aria-hidden', 'false');
      }
    }

    const { x, y } = this.slotXY(yard.locoAt, 0);
    this.locoEl.style.transform = `translate(${x}px, ${y}px)`;
    this.locoEl.classList.toggle('tappable', this.locoTappable);

    for (let t = 0; t < this.roadEls.length; t++) {
      this.roadEls[t].classList.toggle('tappable', this.legalRoads.has(t));
      this.roadEls[t].classList.toggle('occupied', yard.locoAt === t);
    }
  }

  /** Tell the view which taps are live right now, so it can outline exactly those. */
  setTargets(targets: TapTarget[]): void {
    this.legalRoads.clear();
    this.legalCars.clear();
    this.locoTappable = false;
    for (const t of targets) {
      if (t.kind === 'road') this.legalRoads.add(t.track);
      else if (t.kind === 'car' && t.index !== undefined) this.legalCars.add(`${t.track}:${t.index}`);
      else if (t.kind === 'loco') this.locoTappable = true;
    }
    this.paint();
  }

  /** Resolve a DOM event target to the thing that was tapped. */
  hit(target: EventTarget | null): TapTarget | null {
    if (!(target instanceof Element)) return null;
    const loco = target.closest('.loco');
    if (loco) return { kind: 'loco', track: this.yard?.locoAt ?? ROOT };
    const car = target.closest<HTMLElement>('.car');
    if (car && car.dataset.track !== undefined && car.dataset.index !== undefined) {
      return { kind: 'car', track: Number(car.dataset.track), index: Number(car.dataset.index) };
    }
    const road = target.closest<HTMLElement>('.road');
    if (road && road.dataset.track !== undefined) return { kind: 'road', track: Number(road.dataset.track) };
    return null;
  }

  /** A short celebratory pulse on the departure road. Skipped when motion is reduced. */
  celebrate(track: number, reduced: boolean): void {
    if (reduced) return;
    const el = this.roadEls[track];
    if (!el) return;
    el.classList.remove('cheer');
    void el.offsetWidth;
    el.classList.add('cheer');
  }

  /** Flash the loco when a tap could not be honoured. */
  reject(reduced: boolean): void {
    if (reduced) return;
    this.locoEl.classList.remove('nope');
    void this.locoEl.offsetWidth;
    this.locoEl.classList.add('nope');
  }

  geometry(): Geometry {
    return this.geom;
  }

  /** Centre of a car in viewport coordinates — the browser-verification pass taps these. */
  carPixel(id: number): { x: number; y: number } | null {
    const el = this.carEls.get(id);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  roadPixel(track: number): { x: number; y: number } | null {
    const el = this.roadEls[track];
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  destroy(): void {
    this.host.innerHTML = '';
    this.carEls.clear();
    this.roadEls = [];
    this.yard = null;
  }
}

function allCarIds(yard: Yard): number[] {
  const out: number[] = [];
  for (const road of yard.cars) out.push(...road);
  return out.sort((a, b) => a - b);
}

/** The roads a loco standing on `track` may propel into — used to build tap targets. */
export function pushTargets(yard: Yard): number[] {
  return childrenOf(yard.spec, yard.locoAt);
}
